const ALLOWED_HTML_TAGS = new Set([
  "A",
  "B",
  "BR",
  "DEL",
  "DIV",
  "EM",
  "H1",
  "H2",
  "H3",
  "H4",
  "I",
  "LI",
  "MARK",
  "OL",
  "P",
  "SECTION",
  "SPAN",
  "STRONG",
  "TABLE",
  "TBODY",
  "TD",
  "TH",
  "THEAD",
  "TR",
  "U",
  "UL",
]);

const STRIP_CONTENT_TAGS = new Set(["EMBED", "IFRAME", "OBJECT", "SCRIPT", "STYLE", "SVG", "MATH"]);
const ALLOWED_LINK_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:"]);
const ALLOWED_INLINE_STYLE_PROPERTIES = new Set([
  "background",
  "background-color",
  "border",
  "border-bottom",
  "border-collapse",
  "border-left",
  "border-radius",
  "border-right",
  "border-top",
  "box-shadow",
  "color",
  "display",
  "font-size",
  "font-style",
  "font-weight",
  "line-height",
  "margin",
  "margin-bottom",
  "margin-top",
  "padding",
  "text-align",
  "text-decoration",
  "vertical-align",
  "width",
]);
const STYLE_SAFE_VALUE_PATTERN = /^[a-z0-9#(),.%\s:+\-/'"]+$/i;

const unwrapElement = (element: Element) => {
  const parentNode = element.parentNode;
  if (!parentNode) {
    element.remove();
    return;
  }

  while (element.firstChild) {
    parentNode.insertBefore(element.firstChild, element);
  }

  parentNode.removeChild(element);
};

const sanitizeInlineStyle = (styleValue: string) => {
  const sanitizedEntries = styleValue
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [rawProperty, ...rawValueParts] = entry.split(":");
      const property = rawProperty?.trim().toLowerCase();
      const value = rawValueParts.join(":").trim();

      if (!property || !value || !ALLOWED_INLINE_STYLE_PROPERTIES.has(property)) {
        return null;
      }

      const normalizedValue = value.replace(/\s+/g, " ");
      if (
        !STYLE_SAFE_VALUE_PATTERN.test(normalizedValue)
        || normalizedValue.includes("url(")
        || normalizedValue.includes("expression(")
      ) {
        return null;
      }

      return `${property}: ${normalizedValue}`;
    })
    .filter(Boolean);

  return sanitizedEntries.join("; ");
};

const stripWrappedHtmlCodeFence = (value: string): string => {
  const trimmedValue = value.trim();
  const fencedHtmlMatch = trimmedValue.match(/^```(?:html)?\s*([\s\S]*?)\s*```$/i);

  if (!fencedHtmlMatch) {
    return value;
  }

  return fencedHtmlMatch[1].trim();
};

export const sanitizeRichHtml = (htmlContent: string | null | undefined): string => {
  if (!htmlContent) {
    return "";
  }

  const normalizedHtmlContent = stripWrappedHtmlCodeFence(htmlContent);

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return normalizedHtmlContent;
  }

  const parser = new DOMParser();
  const parsedDocument = parser.parseFromString(normalizedHtmlContent, "text/html");

  Array.from(parsedDocument.body.querySelectorAll("*")).forEach((element) => {
    const tagName = element.tagName.toUpperCase();

    if (STRIP_CONTENT_TAGS.has(tagName)) {
      element.remove();
      return;
    }

    if (!ALLOWED_HTML_TAGS.has(tagName)) {
      unwrapElement(element);
      return;
    }

    Array.from(element.attributes).forEach((attribute) => {
      const attributeName = attribute.name.toLowerCase();
      const attributeValue = attribute.value.trim();

      if (attributeName.startsWith("on")) {
        element.removeAttribute(attribute.name);
        return;
      }

      if (tagName === "A" && attributeName === "href") {
        try {
          const url = new URL(attributeValue, window.location.origin);
          if (!ALLOWED_LINK_PROTOCOLS.has(url.protocol)) {
            element.removeAttribute(attribute.name);
          }
        } catch {
          element.removeAttribute(attribute.name);
        }
        return;
      }

      if (tagName === "A" && (attributeName === "target" || attributeName === "rel")) {
        return;
      }

      if (attributeName === "style") {
        const sanitizedStyle = sanitizeInlineStyle(attributeValue);
        if (sanitizedStyle) {
          element.setAttribute("style", sanitizedStyle);
        } else {
          element.removeAttribute(attribute.name);
        }
        return;
      }

      element.removeAttribute(attribute.name);
    });

    if (tagName === "A" && element.getAttribute("href")) {
      element.setAttribute("target", "_blank");
      element.setAttribute("rel", "noopener noreferrer");
    }
  });

  return parsedDocument.body.innerHTML;
};

const normalizeLineBreaks = (value: string) => value.replace(/\r\n?/g, "\n");

const stripControlCharacters = (value: string, preserveLineBreaks: boolean) =>
  value.replace(preserveLineBreaks ? /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g : /[\u0000-\u001F\u007F]/g, "");

export const normalizePlainText = (
  value: string | null | undefined,
  options?: { multiline?: boolean; maxLength?: number }
): string => {
  if (!value) {
    return "";
  }

  const multiline = options?.multiline === true;
  let normalizedValue = stripControlCharacters(normalizeLineBreaks(String(value)), multiline)
    .replace(/\u00A0/g, " ")
    .trim();

  if (multiline) {
    normalizedValue = normalizedValue
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n");
  } else {
    normalizedValue = normalizedValue.replace(/\s+/g, " ");
  }

  if (options?.maxLength && normalizedValue.length > options.maxLength) {
    normalizedValue = normalizedValue.slice(0, options.maxLength).trim();
  }

  return normalizedValue;
};

export const sanitizeWebsiteSlug = (value: string | null | undefined): string => {
  const normalizedValue = normalizePlainText(value, { maxLength: 80 }).toLowerCase();
  return normalizedValue
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
};

export const getSafeUrl = (
  value: string | null | undefined,
  options?: { allowedProtocols?: string[]; baseUrl?: string }
): string | null => {
  if (!value || typeof window === "undefined") {
    return null;
  }

  try {
    const url = new URL(value, options?.baseUrl || window.location.origin);
    const allowedProtocols = new Set(options?.allowedProtocols || Array.from(ALLOWED_LINK_PROTOCOLS));
    if (!allowedProtocols.has(url.protocol)) {
      return null;
    }
    return url.toString();
  } catch {
    return null;
  }
};

export const openUrlInNewTab = (url: string) => {
  const safeUrl = getSafeUrl(url, { allowedProtocols: ["http:", "https:"] });
  if (!safeUrl) {
    return;
  }
  window.open(safeUrl, "_blank", "noopener,noreferrer");
};

export const openUrlInCurrentTab = (url: string) => {
  const safeUrl = getSafeUrl(url);
  if (!safeUrl) {
    return;
  }
  window.location.assign(safeUrl);
};

export const buildGoogleMapsSearchUrl = (latitude: unknown, longitude: unknown): string | null => {
  const numericLatitude = Number(latitude);
  const numericLongitude = Number(longitude);

  if (!Number.isFinite(numericLatitude) || !Number.isFinite(numericLongitude)) {
    return null;
  }

  return `https://www.google.com/maps/search/?api=1&query=${numericLatitude},${numericLongitude}`;
};
